import Button from 'bf-library/Button';
import Card from 'bf-library/Card';
import Checkbox from 'bf-library/Checkbox';
import FeedbackMessage from 'bf-library/FeedbackMessage';
import Input from 'bf-library/Input';
import Title from 'bf-library/Title';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { TextArea } from 'semantic-ui-react';
import AxiosFactory from '../../services/axios/AxiosFactory';
import { getAcceptances } from './../../services/API';
import { initialErrors, isFormValid, validateField, validateForm } from './EnquiryForm.utils';
import LegalAcceptance from './LegalAcceptance/LegalAcceptance';
import OccupancySelector from 'Components/SearchBar/OccupancySelector/OccupancySelector';
import DateRangeSelector from 'Components/SearchBar/DateRangeSelector';
const EnquiryForm = ({ id, container, propertyCode, channelId, language, intl, checkIn, checkOut, ipAddress, userAgent, maxLengthOfStay, minLengthOfStay, childrenEnabled, defaultOccupancy, maxChildAge, minChildAge, maxOccupancy, minOccupancy, }) => {
    let FormState;
    (function (FormState) {
        FormState["INITIAL"] = "initial";
        FormState["SUBMITTED"] = "submitted";
    })(FormState || (FormState = {}));
    ;
    const [checkInDate, setCheckInDate] = useState(checkIn);
    const [checkOutDate, setCheckOutDate] = useState(checkOut);
    const [occupancy, setOccupancy] = useState(defaultOccupancy);
    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [comments, setComments] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [errors, setErrors] = useState(initialErrors);
    const [additionalConditions, setAdditionalConditions] = useState({});
    const [privacyPolicy, setPrivacyPolicy] = useState();
    const [termsAndConditions, setTermsAndConditions] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feedbackMessageProps, setFeedbackMessageProps] = useState({ type: '', message: '' });
    const [formState, setFormState] = useState(FormState.INITIAL);
    const feedbackMessageSuccessProps = useMemo(() => ({
        type: 'success',
        message: intl.formatMessage({
            id: 'bf.enquiryForm.save.success',
            defaultMessage: 'bf.enquiryForm.save.success',
        }),
    }), [intl]);
    const feedbackMessageErrorProps = useMemo(() => ({
        type: 'error',
        message: intl.formatMessage({
            id: 'bf.enquiryForm.save.error',
            defaultMessage: 'bf.enquiryForm.save.error',
        }),
    }), [intl]);
    const resetForm = useCallback(() => {
        setName('');
        setSurname('');
        setEmail('');
        setComments('');
        setAcceptedTerms(false);
        setErrors(initialErrors);
    }, [setName, setSurname, setEmail, setComments, setAcceptedTerms, setErrors]);
    const dismissFeedbackMessage = useCallback(() => {
        setFeedbackMessageProps({ type: '', message: '' });
    }, [setFeedbackMessageProps]);
    const kubeInstance = useMemo(() => {
        const kubeClient = AxiosFactory.createKubeInstance();
        return kubeClient.getInstance();
    }, []);
    useEffect(() => {
        getAcceptances({ propertyCode, language })
            .then((response) => {
            var _a, _b;
            const privacyPolicyContent = ((_a = response.privacyPolicy) === null || _a === void 0 ? void 0 : _a.url) || ((_b = response.privacyPolicy) === null || _b === void 0 ? void 0 : _b.description)
                ? response.privacyPolicy
                : {
                    url: null,
                    description: intl.formatMessage({
                        id: 'gdpr.privacy_policy.default',
                        defaultMessage: 'gdpr.privacy_policy.default',
                    }),
                };
            setAdditionalConditions(response.additionalConditions || {});
            setPrivacyPolicy(privacyPolicyContent);
            setTermsAndConditions(response.termsAndConditions || {});
        })
            .catch(error => {
            console.error('Failed to load legal conditions:', error);
        });
    }, [language, propertyCode, intl]);
    const validateSingleField = useCallback((fieldName, value) => {
        const error = validateField(fieldName, value);
        setErrors(prev => (Object.assign(Object.assign({}, prev), { [fieldName]: error })));
        return error === '';
    }, []);
    const validateEntireForm = useCallback(() => {
        const formData = {
            name,
            surname,
            email,
            checkInDate,
            checkOutDate,
        };
        const newErrors = validateForm(formData);
        setErrors(newErrors);
        return isFormValid(newErrors);
    }, [name, surname, email, checkInDate, checkOutDate]);
    const handleDateChange = useCallback((_, { startDate, endDate }) => {
        dismissFeedbackMessage();
        if (startDate) {
            const checkInDateString = moment(startDate).format('YYYY-MM-DD');
            setCheckInDate(checkInDateString);
            if (errors.checkInDate) {
                validateSingleField('checkInDate', checkInDateString);
            }
            if (checkOutDate && errors.checkOutDate) {
                validateSingleField('checkOutDate', checkOutDate);
            }
        }
        if (endDate) {
            const checkOutDateString = moment(endDate).format('YYYY-MM-DD');
            setCheckOutDate(checkOutDateString);
            if (errors.checkOutDate) {
                validateSingleField('checkOutDate', checkOutDateString);
            }
            if (checkInDate && errors.checkInDate) {
                validateSingleField('checkInDate', checkInDate);
            }
        }
    }, [checkInDate, checkOutDate, errors.checkInDate, errors.checkOutDate, validateSingleField, dismissFeedbackMessage]);
    const handleAdultsChange = useCallback((_, roomIndex, adults) => {
        dismissFeedbackMessage();
        setOccupancy(prev => {
            const newOccupancy = [...prev];
            if (newOccupancy[roomIndex]) {
                newOccupancy[roomIndex] = Object.assign(Object.assign({}, newOccupancy[roomIndex]), { adults });
            }
            return newOccupancy;
        });
    }, [dismissFeedbackMessage]);
    const handleChildrenChange = useCallback((_, roomIndex, numberOfChildren) => {
        dismissFeedbackMessage();
        setOccupancy(prev => {
            const newOccupancy = [...prev];
            if (newOccupancy[roomIndex]) {
                const currentRoom = newOccupancy[roomIndex];
                const currentChildrenCount = currentRoom.children.length;
                if (currentChildrenCount > numberOfChildren) {
                    newOccupancy[roomIndex] = Object.assign(Object.assign({}, currentRoom), { children: currentRoom.children.slice(0, numberOfChildren) });
                }
                else if (currentChildrenCount < numberOfChildren) {
                    const defaultChildAge = 0;
                    const newChildren = [...currentRoom.children];
                    while (newChildren.length < numberOfChildren) {
                        newChildren.push(defaultChildAge);
                    }
                    newOccupancy[roomIndex] = Object.assign(Object.assign({}, currentRoom), { children: newChildren });
                }
            }
            return newOccupancy;
        });
    }, [dismissFeedbackMessage]);
    const handleChildrenAgeChange = useCallback((_, roomIndex, childIndex, age) => {
        dismissFeedbackMessage();
        setOccupancy(prev => {
            const newOccupancy = [...prev];
            if (newOccupancy[roomIndex] && Array.isArray(newOccupancy[roomIndex].children)) {
                const newChildren = [...newOccupancy[roomIndex].children];
                newChildren[childIndex] = age;
                newOccupancy[roomIndex] = Object.assign(Object.assign({}, newOccupancy[roomIndex]), { children: newChildren });
            }
            return newOccupancy;
        });
    }, [dismissFeedbackMessage]);
    const handleNameChange = (event) => {
        const { value } = event.target;
        setName(value);
        dismissFeedbackMessage();
        if (errors.name && value.trim().length > 0) {
            setErrors(prev => (Object.assign(Object.assign({}, prev), { name: '' })));
        }
    };
    const handleSurnameChange = (event) => {
        const { value } = event.target;
        setSurname(value);
        dismissFeedbackMessage();
        if (errors.surname && value.trim().length > 0) {
            setErrors(prev => (Object.assign(Object.assign({}, prev), { surname: '' })));
        }
    };
    const handleEmailChange = (event) => {
        const { value } = event.target;
        setEmail(value);
        dismissFeedbackMessage();
        if (errors.email && value.trim().length > 0) {
            setErrors(prev => (Object.assign(Object.assign({}, prev), { email: '' })));
        }
    };
    const handleCommentsChange = (event) => {
        setComments(event.target.value);
        dismissFeedbackMessage();
    };
    const handleSubmit = useCallback(() => {
        var _a, _b, _c, _d;
        dismissFeedbackMessage();
        if (!validateEntireForm()) {
            return;
        }
        const occupancyData = {
            adults: ((_a = occupancy[0]) === null || _a === void 0 ? void 0 : _a.adults) || 0,
            children: ((_c = (_b = occupancy[0]) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c.length) || 0,
            childAges: ((_d = occupancy[0]) === null || _d === void 0 ? void 0 : _d.children) || [],
        };
        const formData = {
            channelId,
            checkInDate,
            checkOutDate,
            comments,
            name,
            surname,
            email,
            propertyCode,
            language,
            occupancy: occupancyData,
            ipAddress,
            userAgent,
        };
        setIsSubmitting(true);
        kubeInstance
            .post(`/enquiry`, formData)
            .then(() => {
            setIsSubmitting(false);
            resetForm();
            setFeedbackMessageProps(feedbackMessageSuccessProps);
            setFormState(FormState.SUBMITTED);
        })
            .catch(() => {
            setIsSubmitting(false);
            setFeedbackMessageProps(feedbackMessageErrorProps);
        });
    }, [channelId, checkInDate, checkOutDate, comments, email, ipAddress, kubeInstance,
        language, name, occupancy, propertyCode, surname, userAgent, validateEntireForm,
        feedbackMessageSuccessProps, feedbackMessageErrorProps, resetForm, dismissFeedbackMessage]);
    const acceptancesLabel = useMemo(() => (React.createElement(LegalAcceptance, { additionalConditions: additionalConditions, termsAndConditions: termsAndConditions, privacyPolicy: privacyPolicy })), [additionalConditions, termsAndConditions, privacyPolicy]);
    return (React.createElement("div", { className: "enquiry-form-container" },
        formState === FormState.INITIAL && React.createElement("div", null,
            React.createElement(Card, { className: "enquiry-form-panel enquiry-form-stay-panel" },
                React.createElement(Title, { content: (React.createElement(FormattedMessage, { id: "bf.yourStay", defaultMessage: "bf.yourStay" })), size: "large" }),
                React.createElement(OccupancySelector, { container: container || document.body, occupancy: occupancy, childrenEnabled: childrenEnabled, minOccupancy: 1, maxOccupancy: 40, maxChildAge: maxChildAge, minChildAge: minChildAge, maxRoomsPerBooking: 1, allowRooms: false, onChangeAdults: handleAdultsChange, onChangeChildren: handleChildrenChange, onChangeChildrenAge: handleChildrenAgeChange, onChangeRoomsQuantity: () => { }, onRemoveRoom: () => { } }),
                React.createElement(DateRangeSelector, { id: id, checkIn: checkInDate, checkOut: checkOutDate, currentLanguage: language, minLengthOfStay: 1, maxLengthOfStay: 1095, showBlockedDaysReferences: false, onChangeDates: handleDateChange })),
            React.createElement(Card, { className: "enquiry-form-panel enquiry-form-guest-panel" },
                React.createElement(Title, { content: (React.createElement(FormattedMessage, { id: "bf.personalInformation", defaultMessage: "bf.personalInformation" })), size: "large" }),
                React.createElement(Input, { name: "firstName", className: "bf-search-bar-item-label", label: React.createElement(FormattedMessage, { id: "bf.firstName", defaultMessage: "bf.firstName" }), type: "text", value: name, onChange: handleNameChange, onBlur: () => validateSingleField('name', name), required: true, showError: !!errors.name, errorMessage: errors.name }),
                React.createElement(Input, { name: "lastName", className: "bf-search-bar-item-label", label: React.createElement(FormattedMessage, { id: "bf.lastName", defaultMessage: "bf.lastName" }), type: "text", value: surname, onChange: handleSurnameChange, onBlur: () => validateSingleField('surname', surname), required: true, showError: !!errors.surname, errorMessage: errors.surname }),
                React.createElement(Input, { name: "email", className: "bf-search-bar-item-label", label: React.createElement(FormattedMessage, { id: "bf.email", defaultMessage: "bf.email" }), type: "email", value: email, onChange: handleEmailChange, onBlur: () => validateSingleField('email', email), required: true, showError: !!errors.email, errorMessage: errors.email })),
            React.createElement(Card, { className: "enquiry-form-panel enquiry-form-comments-panel" },
                React.createElement(Title, { content: (React.createElement(FormattedMessage, { id: "bf.extraInformation", defaultMessage: "bf.extraInformation" })), size: "large" }),
                React.createElement(TextArea, { name: "comments", className: "bf-search-bar-item-label bf-search-bar-textarea", label: React.createElement(FormattedMessage, { id: "bf.comments", defaultMessage: "bf.comments" }), value: comments, rows: 10, onChange: handleCommentsChange })),
            React.createElement(Checkbox, { value: "terms", label: acceptancesLabel, isChecked: acceptedTerms, onCheck: (_, checked) => {
                    dismissFeedbackMessage();
                    setAcceptedTerms(checked);
                } })),
        feedbackMessageProps.type ? React.createElement(FeedbackMessage, Object.assign({ className: "enquiry-form-feedback-message" }, feedbackMessageProps)) : null,
        formState === FormState.INITIAL && React.createElement(Button, { content: React.createElement(FormattedMessage, { id: "bf.submit", defaultMessage: "bf.submit" }), primary: true, fluid: true, size: "large", className: "enquiry-form-submit-button", onClick: handleSubmit, loading: isSubmitting, disabled: !acceptedTerms || isSubmitting })));
};
export default injectIntl(EnquiryForm);
// i18nLabel[bf.maxLosTooltip]
// i18nLabel[bf.minLosTooltip]
// i18nLabel[bf.noAvailability]
// i18nLabel[bf.noAvailabilityForCheckin]
// i18nLabel[bf.departuresOnly]
// i18nLabel[bf.stayDates]
// i18nLabel[bf.arrivalsOnly]
// i18nLabel[hotel.rateManager.ratePlans.addEditRatePlan.closeForDeparture.label]
// i18nLabel[hotel.rateManager.ratePlans.addEditRatePlan.closeForArrival.label]
// i18nLabel[bf.doneButtonDatePicker]
// i18nLabel[bf.cancel]
// i18nLabel[seekda.chainmetacpc.reset]
// i18nLabel[bf.nightsCount]
// i18nLabel[bf.minNights]
// i18nLabel[bf.enquiryForm.save.success]
// i18nLabel[bf.enquiryForm.save.error]
